/* eslint-disable react-hooks/exhaustive-deps */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-06-10 11:39:10
 *
 *******************************************************/
// @flow
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { withI18n } from '@shopify/react-i18n';
import { connect } from 'react-redux';
import {
  TURBO_LANDING_PAGE_DATA,
  TURBO_LANDING_PAGE_SECTION_TOOLTIP
} from 'src/constants/landing-page';
import { URL } from 'src/constants/links';
import { routes } from 'src/routes';
import { showChatbox } from 'src/integrations/chatbox';
import { Modal } from '@shopify/polaris';
import history from 'src/routes/history';
import { getShop } from 'src/helpers';

import boostTurboLogoSuper from 'src/assets/images/landing-page/turbo/logo.super-charge.svg';
import boostTurboLogoBoost from 'src/assets/images/landing-page/turbo/logo.boost-turbo.svg';
import turboIcon from 'src/assets/images/landing-page/turbo/icon-turbo.svg';
import v3Icon from 'src/assets/images/landing-page/turbo/icon-v3.svg';
import chartImg from 'src/assets/images/landing-page/turbo/chart.svg';
import rightIcon from 'src/assets/images/landing-page/turbo/icon-right.svg';
import contactImg from 'src/assets/images/landing-page/turbo/contact.svg';
import bannerImg from 'src/assets/images/landing-page/turbo/banner.png';
import bannerTextImg from 'src/assets/images/landing-page/turbo/banner-text.svg';
import blurBgImg from 'src/assets/images/landing-page/turbo/blur-bg.svg';
import blurBgIntroImg from 'src/assets/images/landing-page/turbo/blur-bg-intro.png';
import blurBgStepsImg from 'src/assets/images/landing-page/turbo/blur-bg-steps.png';
import backIcon from 'src/assets/images/landing-page/turbo/back.png';
import logo from 'src/assets/images/landing-page/turbo/logo.png';
import logoEffect1 from 'src/assets/images/landing-page/turbo/logo.effect1.png';
import logoEffect2 from 'src/assets/images/landing-page/turbo/logo.effect2.png';
import logoEffect3 from 'src/assets/images/landing-page/turbo/logo.effect3.png';
import closeIcon from 'src/assets/images/landing-page/turbo/close.png';
import playIcon from 'src/assets/images/landing-page/turbo/play.svg';
import videoPreviewImg from 'src/assets/images/landing-page/turbo/video.preview.png';
import speedIcon from 'src/assets/images/landing-page/turbo/speed.svg';

import HowFastImage from './images/how-fast';
import WhatImage from './images/what';
import FaqList from './faqs-list.component';
import EmbeddedVideo from './embeddedVideo';

const TurboLandingPage = props => {
  const { i18n } = props;
  const [isOpenVideo, setOpenVideo] = useState(false);
  const wrapperRef = useRef(null);
  const sendMessageBtnRef = useRef(null);

  const [visibleSection, setVisibleSection] = useState('intro');
  const [isScrolling, setIsScrolling] = useState(false);

  const onBack = () => {
    try {
      const shop = getShop();
      const previousPage = localStorage.getItem(`${shop}-previous-page`);
      if (previousPage) {
        history.push(previousPage);
      } else {
        history.push('/');
      }
    } catch (error) {
      history.push('/');
    }
  };

  const onUpgrade = () => {
    history.push(routes.appPreferences.view());
  };

  const onLearnMore = () => {
    window.open(URL['NewVersionTurbo'], '_blank');
  };

  const onReadMoreFeature = link => {
    window.open(link, '_blank');
  };

  const openChatBox = () => {
    showChatbox();
  };

  const onReadMore = () => {
    window.open(URL['HowToUpgradeTurbo'], '_blank');
  };

  useEffect(() => {
    window.Intercom('update', {
      // hide_default_launcher: true,
      background_color: '#3b0f5a'
    });

    const tooltipElements = document.querySelectorAll('.navigate-tooltip');
    tooltipElements.forEach((tooltipElement, index) => {
      const tooltipValue = tooltipElement.getAttribute('data-tooltip');
      tooltipElement.style.setProperty('--tooltip-content', `"${tooltipValue}"`);
    });

    return () => {
      window.Intercom('update', {
        // hide_default_launcher: false,
        background_color: 'rgb(0, 128, 96)'
      });
    };
  }, []);

  useEffect(() => {
    let elements = document.querySelectorAll(
      '.parallax, .parallax-x-l, .parallax-x-r, .parallax-y'
    );
    let sections = document.querySelectorAll('section');

    let debounceTimeouts = new Map();

    let observer = new IntersectionObserver(
      function (entries, observer) {
        entries.forEach(entry => {
          if (debounceTimeouts.has(entry.target)) {
            clearTimeout(debounceTimeouts.get(entry.target));
          }

          let timeout = setTimeout(() => {
            if (entry.isIntersecting) {
              if (!entry.target.classList.contains('visible')) {
                entry.target.classList.add('visible');
              }
            } else {
              entry.target.classList.remove('visible');
            }
          }, 100);

          debounceTimeouts.set(entry.target, timeout);
        });
      },
      {
        threshold: 0.1
      }
    );

    const sectionObserver = new IntersectionObserver(
      entries => {
        entries.reverse().forEach(entry => {
          if (entry.isIntersecting && !isScrolling) {
            setVisibleSection(entry.target.id);
          }
        });
      },
      { threshold: 0.5 }
    );

    elements.forEach(element => {
      observer.observe(element);
    });

    sections.forEach(section => {
      sectionObserver.observe(section);
    });

    return () => {
      elements.forEach(element => {
        observer.unobserve(element);
      });

      sections.forEach(section => {
        sectionObserver.unobserve(section);
      });

      debounceTimeouts.forEach((timeout, element) => {
        clearTimeout(timeout);
      });
      debounceTimeouts.clear();
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && !sendMessageBtnRef.current.contains(event.target)) {
        window.Intercom('hide');
      }
    };
    const parentNode = wrapperRef.current;
    parentNode.addEventListener('click', handleClickOutside);
    return () => {
      parentNode.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const scrollToSection = useCallback(id => {
    setIsScrolling(true);

    document.getElementById(id).scrollIntoView({ behavior: 'smooth' });
    setTimeout(() => {
      setVisibleSection(id);
    }, 1000);

    setTimeout(() => setIsScrolling(false), 1000); // Reset isScrolling after scroll
  }, []);

  return (
    <div className="BC-Page__Turbo-Landing-Page" ref={wrapperRef}>
      <Modal open={isOpenVideo}>
        <div className="video-modal">
          <div className="inner-content">
            <EmbeddedVideo width="100%" height="100%" />
          </div>
          <div className="close">
            <img src={closeIcon} alt="close" onClick={() => setOpenVideo(false)} />
          </div>
        </div>
      </Modal>
      <section
        id="intro"
        className="intro"
        style={{ background: `url(${blurBgIntroImg}) 50% 50%` }}>
        <div className="container">
          <div className="wrapper">
            <img className="fade-in-1" src={boostTurboLogoSuper} alt="logo" />
            <img className="parallax-y fade-in-2" src={boostTurboLogoBoost} alt="logo" />
            <div className="sub-title parallax fade-in-2">
              {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_Description')}
            </div>
            <div className="action fade-in-3">
              <button onClick={onUpgrade} className="parallax-x-l primary">
                {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_Btn_Upgrade')}
              </button>
              <button onClick={onLearnMore} className="parallax-x-r normal">
                {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_Btn_Learn')}
              </button>
            </div>
          </div>
          <div className="logo-container">
            <img className="parallax logo logo-main" src={logo} alt="logo" />
            <img className="parallax logo-effect-1 logo-effect" src={logoEffect1} alt="logo" />
            <img className="parallax logo-effect-2 logo-effect" src={logoEffect2} alt="logo" />
            <img className="parallax logo-effect-3 logo-effect" src={logoEffect3} alt="logo" />
          </div>
        </div>
      </section>
      <section
        id="what-is"
        className="parallax what-is"
        style={{ background: `url(${blurBgImg}) 80% 0%` }}>
        <div className="container flex">
          <div className="head">
            <div className="left">
              <div className="parallax title">
                {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_What_Title')}
              </div>
              <div className="description">
                {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_What_Description')}
              </div>
              <div className="action">
                <button onClick={onUpgrade} className="parallax-x-l primary">
                  {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_Btn_Upgrade')}
                </button>
              </div>
            </div>
            <div className="parallax-x-r right">
              <WhatImage />
            </div>
          </div>
        </div>
      </section>
      <section
        id="how-fast"
        className="parallax how-fast"
        style={{ background: `url(${blurBgImg}) 80% 0%` }}>
        <div className="container">
          <div className="head flex">
            <div className="left">
              <div className="parallax title">
                {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_How_Title')}
              </div>
              <div className="description">
                {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_How_Description', {
                  percent: (
                    <span
                      style={{
                        color: '#ca32db'
                      }}>
                      20%
                    </span>
                  )
                })}
              </div>
              <div className="action">
                <button onClick={onUpgrade} className="parallax-x-l primary">
                  {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_Btn_Upgrade')}
                </button>
              </div>
            </div>
            <div className="right">
              <HowFastImage />
            </div>
          </div>
          <div className="block-compare">
            <div className="head">
              <div
                className="parallax title"
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <img src={speedIcon} alt="" width={22} />
                <span>{i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_How_Subtext')}</span>
              </div>
              <div className="versions">
                <div className="parallax-x-l turbo">
                  <img src={turboIcon} alt="" />
                  <span>{i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_Turbo')}</span>
                </div>
                <div className="parallax-x-r v3">
                  <img src={v3Icon} alt="" />
                  <span>{i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_V3')}</span>
                </div>
              </div>
            </div>
            <div className="chart">
              <img src={chartImg} alt="" />
            </div>
            <div className="note">
              {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_How_Note')}
            </div>
          </div>
        </div>
      </section>
      <section
        id="why"
        className="parallax why"
        style={{ background: `url(${blurBgImg}) 40% 20%` }}>
        <div className="container">
          <div className="head">
            <div className="parallax title">
              {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_Why_Title')}
            </div>
            <div className="parallax description">
              {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_Why_Description')}
            </div>
          </div>
          <div className="list">
            {TURBO_LANDING_PAGE_DATA(i18n).Features.map((item, index) => {
              const { title, description, icon, link } = item;
              return (
                <div className="parallax item darksoul-gradient-card1" key={index}>
                  <div className="darksoul-content-card">
                    <div className="parallax-x-l image">
                      <img src={icon} alt="" width="100" height="auto" />
                    </div>
                    <div className="item-title">{i18n.translate(title)}</div>
                    <div className="item-description">{i18n.translate(description)}</div>
                    {link && (
                      <div className="item-action" onClick={() => onReadMoreFeature(link)}>
                        <span> {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_ReadMore')}</span>
                        <img src={rightIcon} alt="" width={14} />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <section id="steps" className="steps" style={{ background: `url(${blurBgStepsImg}) 22% 0%` }}>
        <div className="container">
          <div className="head flex">
            <div className="left">
              <div className="parallax title">
                {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_Steps_Title')}
              </div>
              <div className="description">
                {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_Steps_Description')}
              </div>
              <div className="action">
                <button onClick={onUpgrade} className="parallax-x-l primary">
                  {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_Btn_Upgrade')}
                </button>
                <button onClick={onReadMore} className="parallax-x-r normal">
                  {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_Btn_Learn')}
                </button>
              </div>
            </div>
            <div className="right">
              <div className="play">
                <img src={playIcon} alt="play" onClick={() => setOpenVideo(true)} />
              </div>
              <img src={videoPreviewImg} alt="video" width="656" height="369" />
            </div>
          </div>
          <div className="list">
            {TURBO_LANDING_PAGE_DATA(i18n).Steps.map((item, index) => {
              const { title, description, icon } = item;

              return (
                <div className="step" key={index}>
                  <div className="parallax-x-l icon">
                    <img src={icon} alt="" width="118" height="auto" />
                  </div>
                  <div className="step-content">
                    <div className="step-title">
                      {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_Steps_Item')} {index + 1}
                    </div>
                    <div className="parallax step-label">{i18n.translate(title)}</div>
                    <div className="step-description">{description}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <section id="faq" className="faq" style={{ background: `url(${blurBgImg})` }}>
        <div className="container">
          <div className="head">
            <div className="parallax title">
              {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_Faq_Title')}
            </div>
            <div className="description">
              {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_Faq_Description')}
            </div>
          </div>
          <FaqList i18n={i18n} />
        </div>
      </section>
      <section
        id="contact"
        className="contact"
        style={{ background: `url(${blurBgStepsImg}) 50% 0` }}>
        <div className="container">
          <div className="head">
            <div className="left">
              <div className="parallax title">
                {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_Contact_Title')}
              </div>
              <div className="description">
                {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_Contact_Description')}
              </div>
              <div className="action">
                <button
                  onClick={openChatBox}
                  className="parallax-x-l primary"
                  ref={sendMessageBtnRef}>
                  {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_Btn_Send')}
                </button>
              </div>
            </div>
            <div className="parallax-x-r right">
              <img src={contactImg} alt="" />
            </div>
          </div>
        </div>
      </section>
      <section id="banner" className="banner" style={{ background: `url(${blurBgImg})` }}>
        <div className="container">
          <div className="wrapper">
            <img className="banner-bg" src={bannerImg} alt="" />
            <div className="content">
              <div className="parallax title">
                <img src={bannerTextImg} alt="" />
              </div>
              <div className="action">
                <button onClick={onUpgrade} className="parallax-x-l primary">
                  {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_Btn_Upgrade')}
                </button>
                <button onClick={onLearnMore} className="parallax-x-r normal">
                  {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_Btn_Learn')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      <div className="footer">
        <div className="container">
          <div className={`back primary`} onClick={onBack}>
            <img src={backIcon} alt="back" width={16} height={16} />
            <span> {i18n.translate('AdminHTML.LandingPage.Turbo.Turbo_Btn_Back')}</span>
          </div>
        </div>
      </div>
      <div id="navigate" className="navigate">
        {TURBO_LANDING_PAGE_SECTION_TOOLTIP.map((section, idx) => {
          const { id, title } = section;
          return (
            <div
              key={idx}
              className={`navigate-tooltip item ${visibleSection === id ? 'active' : ''}`}
              data-tooltip={i18n.translate(title)}
              onClick={() => scrollToSection(id)}>
              <div className="circle"></div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
const mapDispatchToProps = (dispatch, ownProps) => ({});

const mapStateToProps = (state, props) => {
  return {};
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(TurboLandingPage));
